import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import LogoFallback from "/assets/img/bubbanlogo.jpg";
import axios from "axios";

export default function AdmAdv({
  logoSrc = LogoFallback,
  accent = "#115e32",
  pageTitle = "Admission Advertisement | Bandarban University",
}) {
	const [activeTab, setActiveTab] = useState("bachelor");
	const [ads, setAds] = useState({ bachelor: [], master: [] });
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	
	const advPost = async () => {
		setLoading(true);
		setError(null);

		try {
			const payload = {
				userId: import.meta.env.VITE_API_USER,
				password: import.meta.env.VITE_API_PASS,				
		    };
			const api = axios.create({
				baseURL: import.meta.env.VITE_API_BASE_URL,
				headers: { "Content-Type": "application/json" },
			});
			const response = await api.post("/advertisement", payload);
			
			setAds(response.data);
		} catch (err) {
		   setError(err.message || "Something went wrong");		   
		} finally {
		  setLoading(false);
		}
	};
	useEffect(() => {		
		advPost();
	}, []);
		 
	useEffect(() => {
		document.title = pageTitle;
	}, [pageTitle]);

	const renderAds = (list) => {
		if (!list || list.length === 0) {
		  return (
			<p className="text-gray-500 text-center py-10">No advertisements found.</p>
		  );
		}

		const [latest, ...archive] = list;

    return (
      <>
        {/* Top Section (Latest Ad) */}
        <motion.div
          className="bg-white rounded-2xl shadow-lg p-6 mb-12"
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-2xl font-bold text-indigo-700 mb-4">
            Latest Advertisement
          </h2>
          {latest.type === "pdf" ? (
            <iframe
              src={`${import.meta.env.VITE_API_SOURCE_URL}${latest.file_1}`}
              title="Advertisement PDF"
              className="w-full h-[600px] rounded-lg border"
            ></iframe>
          ) : (
            <img
              src={`${import.meta.env.VITE_API_SOURCE_URL}${latest.file_1}`}
              alt="Advertisement"
              className="w-full rounded-lg shadow"
            />
          )}
          <p className="mt-3 text-gray-600">{latest.title}</p>
        </motion.div>

        {/* Archive Section */}
        {archive.length > 0 && (
          <div>
            <h3 className="text-xl font-semibold text-gray-800 mb-4">
              Archive Advertisements
            </h3>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {archive.map((ad, idx) => (
                <motion.div
                  key={idx}
                  className="bg-white rounded-xl shadow hover:shadow-lg transition p-4 flex flex-col"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <h4 className="text-indigo-700 font-semibold mb-2">
                    {ad.title}
                  </h4>
                  {ad.type === "pdf" ? (
                    <a
                      href={`${import.meta.env.VITE_API_SOURCE_URL}${ad.file_1}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-indigo-600 hover:underline mt-auto"
                    >
                      View PDF
                    </a>
                  ) : (
                    <img
                      src={`${import.meta.env.VITE_API_SOURCE_URL}${ad.file_1}`}
                      alt={ad.title}
                      className="rounded-lg border mt-auto"
                    />
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-green-700 to-teal-700 text-white py-16 text-center">
        <motion.h1
          className="text-4xl md:text-5xl font-bold mb-3"
          initial={{ opacity: 0, y: -40 }}
          animate={{ opacity: 1, y: 0 }}
        >
          Admission Advertisements
        </motion.h1>
        <motion.p
          className="max-w-2xl mx-auto text-lg opacity-90"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          Stay updated with the latest admission circulars and archives of Bandarban University.
        </motion.p>
      </section>

      {/* Tabs */}
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex justify-center mb-8">
          <button
            onClick={() => setActiveTab("bachelor")}
            className={`px-6 py-2 rounded-l-lg font-medium ${
              activeTab === "bachelor"
                ? "bg-green-700 text-white"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
          >
            Bachelor Program
          </button>
          <button
            onClick={() => setActiveTab("master")}
            className={`px-6 py-2 rounded-r-lg font-medium ${
              activeTab === "master"
                ? "bg-green-700 text-white"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
          >
            Master Program
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === "bachelor" && renderAds(ads.bachelor)}
        {activeTab === "master" && renderAds(ads.master)}
      </div>
    </div>
  );
}
